import * as PIXI from "pixi.js";
import FXManager from "../Effects/FXManager";
import { EASING_FNS } from "../Collection/BezierPath";
import { Settings } from "../../settings";
import { soundManager } from "../../SoundManager";
import { SOUND_NAME } from "../../sounds";

// Reusable point — one per animator, so concurrent trails never share it.
const _tempPoint = new PIXI.Point();

// ── Per-frame sound dedup ────────────────────────────────────────────────────
// A volley fires every bolt in ONE frame — the Golden Mystery hits every coin
// at once, which is a dozen or more. Twelve copies of the same 0.5s one-shot
// starting together is twelve times the level and a comb filter, not an effect.
// A volley is one throw to the ear, so it is one sound.
// Same proven pattern as fireLightningBolt.js and useLandingAnimations.js.
const _frameSoundsPlayed = new Set();
let _frameSoundRAF = null;

function _playOncePerFrame(soundName) {
  if (_frameSoundsPlayed.has(soundName)) return;
  _frameSoundsPlayed.add(soundName);
  soundManager.play(soundName);
  if (!_frameSoundRAF) {
    _frameSoundRAF = requestAnimationFrame(() => {
      _frameSoundsPlayed.clear();
      _frameSoundRAF = null;
    });
  }
}

/**
 * Travel time, ms. One number for every hop — the distance-derived version was
 * a mistake and is gone.
 *
 * Trail LENGTH is the reason. Particles live a fixed 0.2s, so the streak is
 * whatever slice of the path the head covers in that time: faster travel makes
 * a LONGER streak, not a shorter one. Deriving the time from distance drove the
 * short hops onto a floor, and there the streak covered the whole path and then
 * hung in the air after landing.
 *
 *   300  current — slower, so the hop is watchable
 *   200  the previous value, quick enough that it read as a flicker
 *   230  before that
 *   110  what the short hops were getting — the enormous one
 *
 * ── Raising this ALONE makes the streak shorter, not longer ──
 *
 * The streak is a slice of TIME, not of distance: a particle lives
 * TRAIL_PARTICLE_LIFE seconds whatever the flight, so a longer flight means it
 * covers less of the path. Going 200 -> 300 without touching the life turned a
 * comet into a bead with a stub behind it.
 *
 * They can no longer drift: TRAIL_PARTICLE_LIFE is DERIVED from this and
 * TRAIL_STREAK_SHARE, so changing this number keeps the tail the same share of
 * the path and only the pace moves. Tune the look with TRAIL_STREAK_SHARE.
 *
 * If it ever needs to be BOTH faster and shorter, this knob cannot do it: the
 * only lever left is `durationMin`/`durationMax` on `trail_blue` in
 * trail.json, which is the particle's own 0.2s life.
 *
 * ── THE ONE NUMBER. Do not override it per caller ──
 *
 * A trail is a single effect across the whole game: the free-spins level pips
 * and the Golden Mystery's multiplier bolts are the same bolt, and they must
 * fly for the same time. The multiplier used to ask for 320 here and read as a
 * different, slower effect with a stubbier tail — the streak is a fixed slice
 * of TIME, so changing the flight changes the look, not just the pace.
 *
 * `options.duration` is kept for a caller that genuinely is not this effect.
 * Nothing in this game is.
 */
const DEFAULT_DURATION = 300;

/**
 * HOW MUCH OF THE PATH THE TAIL COVERS. This is the knob.
 *
 * ── Why a share and not a lifetime ──
 *
 * The streak is a slice of TIME: a particle lives N seconds whatever the
 * flight, so the length it draws is `life / duration` of the path — a
 * FRACTION, the same on a short hop and a long one. That made the lifetime a
 * number nobody could read: 0.18 means nothing until you divide it by the
 * flight time, and every change to one demanded the same change to the other.
 * The note on DEFAULT_DURATION warns about it three times, which is a sign the
 * shape was wrong rather than that people were careless.
 *
 * So the share is the constant now and the lifetime is derived from it. Change
 * this one number; the pair cannot drift.
 *
 *   0.40  current — a head with a tail behind it, a gap back to the origin
 *   0.60  the previous value, and what shipped until 2026-09-21. Vladimir:
 *         the trail "always connects 2 places", he wanted a projectile
 *   1.00  the tail reaches the origin — a drawn line, not a projectile
 *
 * Lower is more projectile, higher is more comet. Below ~0.2 the streak is a
 * stub and the effect reads as a bead.
 *
 * A straight trail lays its tail exactly over the path the head just flew,
 * which is why it reads longer than the number suggests — the Golden Mystery's
 * curved one looks shorter at the same share because the tail bends away from
 * the head. If this still reads long after tuning, the lever is the CURVE, not
 * this.
 */
const TRAIL_STREAK_SHARE = 0.4;


/**
 * Particle lifetime in REAL SECONDS, derived — see TRAIL_STREAK_SHARE.
 *
 * Overridden per emitter, so trail.json is untouched and `trail_gold` in Pot
 * Collection keeps the length it was authored with.
 */
const TRAIL_PARTICLE_LIFE = (TRAIL_STREAK_SHARE * DEFAULT_DURATION) / 1000;

/**
 * `fx_dot.png` at its native size, from `effects/trail/trail_atlas.json` — 47x47.
 *
 * The one number that turns "how big should the ball be, in pixels" into an
 * emitter scale, because the emitter scales the TEXTURE and knows nothing about
 * pixels.
 */
const TRAIL_DOT_PX = 47;

/**
 * How much of that 47px frame is the BALL, and not the halo around it.
 *
 * Measured off the atlas, by brightness — these particles blend additively, so
 * brightness is what the eye reads as the edge:
 *
 *   >= 200   7 x 7      the white core
 *   >= 128  11 x 11
 *   >=  64  17 x 16     what reads as the ball
 *   >=  25  21 x 21     the last of the glow
 *
 * So the drawn dot is a small bright bead in a mostly-empty square, and sizing
 * the FRAME is not sizing the ball: at a 75px frame the bead is about 30px. That
 * is the whole reason this number kept coming out thin however high the multiple
 * went — 1.5 pips of frame was HALF a pip of ball.
 *
 * 0.4 is the >=64 row: the extent a player would point at and call the ball.
 */
const TRAIL_DOT_CORE = 0.4;

/**
 * A little THINNER than a pip.
 *
 * 1.06 — a hair fatter than a pip — was tuned while the streak was a CONE:
 * every particle was born at 0.62 width and grew to full over its life, so this
 * number was the width of the tail alone and the bolt averaged well under it.
 * Straightening the taper to 1 (see TRAIL_TAPER) made the whole length carry
 * that tail width, and the same number that read as delicate became heavy.
 *
 *   1.06  the cone's tail width — too thick once the cone was removed
 *   0.88  straightened, still heavy
 *   0.80  the even bolt, finer than the pip it flies into
 *   0.96  current — 0.80 + 20%, client feedback from demo 1: "particles flying
 *         to the Tier panel should be 20% bigger"
 *
 * ── It moves EVERY trail, on purpose ──
 *
 * The client named the tier-panel charge, but this constant is shared with the
 * Golden Mystery's multiplier bolts, and the two are deliberately one effect —
 * one flight time, one particle life, one size. Splitting them here is how they
 * drifted apart once before. See the ONE NUMBER note on DEFAULT_DURATION.
 *
 * The old reasoning for going over a pip — that an additive glow with soft
 * edges does not carry the same weight as a solid lit pip — applied to a shape
 * that was only full-width at one end. A bar that is full-width along its whole
 * length does not need the compensation.
 *
 * This is the BALL, measured across, in pips.
 *
 * It is the visible bead, not the texture frame the bead sits in — see
 * TRAIL_DOT_CORE. That distinction is the reason this number is small now: every
 * earlier value on this line was setting the FRAME, so the same 1.1 was drawing a
 * ball of 11px against a 25px pip. Once it meant the ball, 2.3 pips of bead was
 * far more than the effect wants.
 *
 * It rides on the pip, so it stays a fixed relationship at every scale: raise
 * this and the phone gets exactly the same picture, larger, at once.
 *
 * 1.10, was 0.96 — which was a hair SMALLER than the pip it leaves. Vladimir,
 * 2026-09-24: *"a tiny bit bigger than the actual pip."* TRAIL_SPAWN_FREQUENCY
 * needs no change: it has to follow the ball DOWN (gaps open around a thinner
 * bead), and a wider bead at the same rate only overlaps more.
 */
const TRAIL_BALL_IN_PIPS = 1.1;

/**
 * The cell height the trail's particle scale is normalised against — this
 * game's own native symbol height, so scale is 1 at native size.
 *
 * The 266 fallback is Zeus's value and is only a floor for a game that has not
 * set one. Hardcoding 266 here is what made the trail a dot: Mad Labs' cells
 * are 194 native, so every trail came out ~27% under, and on a phone — where
 * the cell is already small — that compounds into a speck.
 */
const TRAIL_REFERENCE_HEIGHT = Settings.SYMBOL_HEIGHT || 266;

/**
 * Which emitters out of the trail bundle — BLUE ONLY.
 *
 * ── The pair was tried, and the pair is what looked wrong ──
 *
 * The bundle ships two: `trail_blue`, one flat blue, and `trail_gold`, which
 * runs orange to yellow over each particle's life. Both were fired on one
 * wrapper so they spawned along the same path on the same frames and the beads
 * interleaved.
 *
 * Interleaved is the problem. The blend is ADDITIVE, so a warm bead landing on
 * a cool one does not sit beside it — the two SUM, and blue plus orange sums
 * toward green. Vladimir, 2026-09-17: "in combination those 2 look too green
 * and dont combine well, so go with blue only."
 *
 * So gold is off. Retuning its tint was the alternative and was not taken: two
 * emitters of the same colour cost two emitters to look like one.
 *
 * Named explicitly because FXManager has no default — a missing emitter returns
 * null and draws nothing, silently. Pot Collection is unaffected: it asks for
 * `trail_gold` by name through `options.emitterName`, which bypasses this.
 */
const TRAIL_EMITTERS = ["trail_blue"];

/**
 * Tint repairs, applied per emitter on top of the bundle.
 *
 * ── `trail_blue` has NO tintEnd in trail.json ──
 *
 * `useTint` is true and `tintStart` is 0x1871b9, but `tintEnd` is null. RevoltFX
 * does not treat that as "no change": `Color.setRgb` computes the deltas as
 * `((targetRgb >> 16) & 0xff) - r`, and `null >> 16` is 0, so every blue bead
 * is tweened from blue to BLACK across its life.
 *
 * On an ADDITIVE blend black is invisible, and the particle is already fading
 * out on its own alpha ramp (alphaStart 1 -> alphaEnd 0). So the blue was being
 * faded twice and died about half way down the streak, while the gold — whose
 * tint BRIGHTENS, orange to yellow — held its colour the whole way. The pair
 * was never mixing evenly; the comment above describing `trail_blue` as "one
 * flat blue" was describing the intent, not what shipped.
 *
 * Flat means start and end the same. Written here rather than in the bundle
 * because the bundle is shared and Pot Collection reads `trail_gold` out of it
 * — the same rule the spawn and scale overrides already follow.
 *
 * ── This is also where to change the colours ──
 *
 * Add a `trail_gold` entry to retune the warm half. Nothing else in the game
 * sets these, so a value here is the whole answer.
 */
const TRAIL_TINTS = {
  trail_blue: { tintStart: 0x1871b9, tintEnd: 0x1871b9 },
};

/**
 * ── The shape of the streak ──
 *
 * Three numbers, all overriding trail.json for this animator only. The bundle
 * is shared, and Pot Collection's own coin travels read `trail_gold` from it, so
 * nothing here is written back to the file.
 *
 * SPAWN COUNT — particles per spawn cycle, per emitter. The bundle says 30, and
 * that was 30 blue. Two emitters at 15 keep the same total.
 *
 * SPAWN RADIUS — the circle each particle is born on, and the whole width of
 * the beam. The bundle's 5 is multiplied by the trail's own scale (2.6 x the
 * cell ratio), so it was spraying particles up to ~13px either side of the
 * path: a band, not a dart. Halved.
 *
 * SCALE — the bundle grows every particle from ~0.55 to ~1.5 over its life.
 * That growth is the CONE: a particle's age is also its distance back down the
 * path, so the oldest and fattest ones are all at the tail. Ending near where
 * it starts keeps the streak the same width from head to tail, and a touch of
 * growth is kept so the tail still softens instead of stopping dead.
 *
 * All four scale ends move together — min and max, start and end — or the
 * particles disagree with each other about how fat they are.
 */
/**
 * ── ONE density, for every trail in the game ──
 *
 * Was 12, and briefly 12-thinned-by-volley-size, which was a mistake: it made a
 * level pip and a Golden Mystery bolt look like different effects, and this
 * whole file exists to keep them one. Vladimir, 2026-09-10: "make all trails
 * identical, unified and performant."
 *
 * ── READ THIS AS A PER-EMITTER NUMBER ──
 *
 * It is handed to every emitter in TRAIL_EMITTERS, so the particles actually in
 * the air are this TIMES the number of emitters. That list was a pair until
 * 2026-09-17 and is now blue alone, so this doubled from 6 to 12 on the same
 * day: one emitter at 12 puts exactly as many beads on the path as two at 6
 * did. The streak keeps its density, the budget below is unchanged, and the
 * only thing that went away is the colour nobody wanted.
 *
 * A trail holds `count / frequency * life` particles in the air —
 * 12 / 0.008 * 0.18 = 270 per bolt. That matters because the two callers put
 * very different numbers of bolts up at once:
 *
 *   level pips           fired ONE at a time (BonusGameView awaits each)
 *   multiplier bolts     fired ALL at once, one per coin on the board
 *
 * The budget is one formula: `count / frequency * life * bolts`, which is
 * `count * 22.5 * bolts` at the current frequency and life. Two measured
 * points fix the ceiling — 12 x 22.5 x 12 = 3,240 STUTTERED, 4 x 22.5 x 12 =
 * 1,080 was smooth. Those were measured with the emitter PAIR, so read them as
 * totals: the stutter was 3,240 beads in the air however many emitters made
 * them. Today's 12 on one emitter is 1,620 at twelve bolts — the same total the
 * pair produced at 6 each, and half the bad number.
 *
 * ── The number to watch is BOLTS, not this ──
 *
 * `sources x targets` is every multiplier times every coin and collector on the
 * board, so a heavy Golden Mystery can fire far more than twelve. The
 * "golden trail volley" diag line reports the count and the live-particle
 * estimate on every volley; if a real one goes past ~2,000 and stutters, come
 * back here rather than guessing. Raise this only with that line in hand.
 */
const TRAIL_SPAWN_COUNT = 12;

/**
 * Seconds between spawn bursts — how far apart the beads land.
 *
 * The bundle fires every 0.01s. At a charge's ~3.3 px/ms that is a burst every
 * 33px along the path, and a ball is ~28px across — they overlap into a solid
 * rope, and two emitters interleaved that tightly average blue and gold into one
 * colour.
 *
 * 0.016 pulled them 53px apart, which is a dotted line: separate beads, but the
 * trail stopped reading as one thing. 0.011 was the middle — about 36px, so
 * they just touched. Continuous, with the two colours still telling themselves
 * apart.
 *
 * ── It has to move whenever the BALL does ──
 *
 * What keeps the bolt solid is the spacing measured AGAINST the bead's width,
 * not the spacing on its own. TRAIL_BALL_IN_PIPS came down from 1.06 to 0.80,
 * so at 0.011 the same 36px gaps would have opened up around a bead a quarter
 * narrower and the rope would have gone dotted — the 0.016 failure, arrived at
 * from the other direction. This tracks it: ~27px between beads that are ~25%
 * thinner, so it stays exactly as continuous as it was, just finer.
 *
 *   ball 1.06 -> 0.011    ball 0.88 -> 0.009    ball 0.80 -> 0.008
 *
 * It is TIME, not distance, so a shorter hop gets FEWER beads rather than
 * tighter ones. That is the right way round: a trail is a rate of fire.
 */
const TRAIL_SPAWN_FREQUENCY = 0.008;


/**
 * The streak's shape, as fractions of its WIDTH.
 *
 * TAPER — how wide a particle is BORN, against the width it dies at. Anything
 * below 1 is a cone, and not by accident: a particle's age is also how far back
 * down the path it sits, so a scale that climbs over its life puts the thin new
 * ones at the nose and the fat old ones at the tail. The bundle grows every
 * particle threefold, which is the wedge this started as.
 *
 *   1.00  current — born and dying at the same width, so the bolt is a straight
 *         bar of even thickness end to end
 *   0.62  the previous value, a mild cone
 *   3.00  the bundle's own, the full wedge
 *
 * At 1 the head is now as wide as the tail was, so the whole trail reads a
 * little heavier — that width is _trailBallPx's, calibrated a hair over a pip,
 * so it is the size that was always intended. Nothing else compensates for the
 * taper, so this is the only line that has to change.
 *
 * SPREAD — the radius of the circle each particle is born on, so the bolt has a
 * soft edge rather than a drawn line. The bundle's own value worked out at about
 * this fraction of the width, which is why it is expressed as one.
 */
const TRAIL_TAPER = 1;
const TRAIL_SPREAD = 0.07;

/**
 * ── Trail size, where there is no pip to measure ──
 *
 * The base game's board has no level boxes, so its bolt is sized the house way
 * instead: a constant times the responsive ratio.
 *
 *   fireLightningBolt      BOLT_CONFIG.BALL_BASE_SCALE * responsiveScale
 *   PotCollectionValueTraveler  (symbolHeight / native) * 1.0
 *   here                   TRAIL_FALLBACK_THICKNESS_PX * (symbolHeight / native)
 *
 * The constant decides how big it reads; the ratio makes every device the same
 * picture at a different size. Neither is allowed to clamp the other — there was
 * a floor here once, and a floor IS zero scaling, which is the one thing a
 * responsive effect must never be.
 *
 * 24px of BALL at native, so a 0.82 board draws it at 20 — which is what the
 * base game was already showing before any of this became measurable.
 */
const TRAIL_FALLBACK_BALL_PX = 24;

/**
 * LevelChargingAnimator — sends a trail from a landed scatter to the level box
 * it charges, and calls back when it arrives.
 *
 * ── The same trail the coins use, carrying nothing ──
 *
 * This is PotCollectionValueTraveler.travelWithTrail with everything to do with
 * VALUES removed. That one moves a cloned PIXI.Text along a Bézier arc with an
 * FXManager trail following it; a scatter has no value to deliver, so the
 * wrapper travels empty and the trail is the whole effect.
 *
 * What is left is small on purpose: an empty container walked in a STRAIGHT
 * line by a ticker, with `emitTrail` targeting it so particles spawn wherever
 * it currently is. No text, no clone, no auto-fit, no scale ramp — and no arc,
 * because the reference game fires these like arrows.
 *
 * Deliberately NOT the lightning bolt. `fireLightningBolt` is the Zeus-to-pot
 * charge and reads as a strike; this reads as something being carried, which is
 * what charging a level box is.
 *
 * Usage:
 *   const animator = new LevelChargingAnimator(trailContainer, bonusSpine, w, h);
 *   await animator.fireTrail(startPos, boneName, { onArrival });
 */
class LevelChargingAnimator {
  /**
   * @param {PIXI.Container} trailContainer Container the trail renders into.
   * @param {object|null}    bonusSpine     bonus_gameview Spine, for bone lookups.
   * @param {number}         symbolWidth    Current cell width.
   * @param {number}         symbolHeight   Current cell height.
   */
  constructor(trailContainer, bonusSpine, symbolWidth, symbolHeight, getDimensions = null) {
    this.trailContainer = trailContainer;
    this.bonusSpine = bonusSpine;
    this.symbolWidth = symbolWidth;
    this.symbolHeight = symbolHeight;
    // Optional live source. See _syncDimensions.
    this._getDimensions = getDimensions;
    // One pip on screen, in px, when the owner can measure one. The bolt's
    // ball size — see _trailBallPx.
    this.pipSize = 0;
    this._active = [];
    this._cancelled = false;
  }

  /**
   * Re-read the cell size from the owner, if it offered a getter.
   *
   * PULLED at fire time rather than pushed on resize, which is what
   * PotCollectionValueTraveler does and why its trails survive a viewport
   * change. A pushed value is only as fresh as the last effect that ran, and
   * the trail scale and the arc height are both measured in cells — stale
   * numbers make a mobile trail a dot.
   */
  /**
   * How thick the bolt is, as an emitter scale.
   *
   * ── The PIP is the ruler, when there is one ──
   *
   * A level trail flies from a symbol to a PIP, and the pips do not scale on the
   * reels' rule — the bonus scene has its own. Sized off the cell, the bolt
   * therefore drifted against the thing it was aiming at, and the drift is worst
   * exactly where it shows: on a phone, where the board shrinks hardest and the
   * level bar least.
   *
   * So the ball is measured live from the box, as a multiple of one pip — see
   * TRAIL_BALL_IN_PIPS. The number is its WIDEST point, which the ramp reaches
   * at the end of a particle's life.
   *
   * ── The fallback is the cell ──
   *
   * The base game has no pips at all: its Golden Mystery multiplier fires the
   * same trail across the reels. There the house rule stands — a constant times
   * the cell ratio, unclamped, which is what fireLightningBolt and
   * PotCollectionValueTraveler both do.
   *
   * @returns {number}
   */
  _trailBallPx() {
    if (this.pipSize > 0) return this.pipSize * TRAIL_BALL_IN_PIPS;
    return (
      TRAIL_FALLBACK_BALL_PX * (this.symbolHeight / TRAIL_REFERENCE_HEIGHT)
    );
  }

  _syncDimensions() {
    if (!this._getDimensions) return;
    const dims = this._getDimensions();
    if (dims?.pipSize) this.pipSize = dims.pipSize;
    if (dims?.symbolWidth) this.symbolWidth = dims.symbolWidth;
    if (dims?.symbolHeight) this.symbolHeight = dims.symbolHeight;
    if (dims?.spine && dims.spine !== this.bonusSpine) {
      // The bonus scene rebuilds its spine on resize. A stale reference sends
      // every bone lookup to a destroyed skeleton, and the trail then has
      // nowhere to fly to.
      this.bonusSpine = dims.spine;
    }
  }

  /**
   * Send one trail from a symbol to a level box.
   *
   * Every early return still fires `onArrival` and resolves. A trail that
   * cannot be drawn must never strand the sequence holding the UI lock open —
   * the bulb lighting matters more than the effect that announces it.
   *
   * @param   {{x:number,y:number}} startPos  Symbol centre, in trailContainer space.
   * @param   {{x:number,y:number}} endGlobal Target in GLOBAL space — the bulb
   *   about to light, not the middle of the box.
   * @param   {object}   [options]
   * @param   {Function} [options.onArrival] Called the moment it lands.
   * @param   {number}   [options.duration]  Travel time override, ms.
   * @param   {number}   [options.bow] Sideways offset at the midpoint, in this
   *          container's pixels. 0 (the default) is a straight line, which is
   *          what every caller but one wants. A positive value bows one way and
   *          a negative value the other, consistently left/right on screen
   *          whichever direction the trail travels — so a caller firing several
   *          trails along ONE axis can alternate the sign and pull them apart.
   *          See fireMultiplierTrails, the only caller that does.
   * @returns {Promise<void>} resolves on arrival.
   */
  fireTrail(startPos, endGlobal, options = {}) {
    this._syncDimensions();
    const container = this.trailContainer;
    if (this._cancelled || !container || container.destroyed || !startPos) {
      options.onArrival?.();
      return Promise.resolve();
    }

    const endPos = this._toLocal(endGlobal);
    if (!endPos) {
      options.onArrival?.();
      return Promise.resolve();
    }

    // ── The throw ──
    //
    // Here and nowhere else, for the same reason the duration lives here: every
    // trail in the game is ONE effect, so it makes ONE sound and no caller can
    // give it a different one. Below the early returns above, so a hop that
    // cannot be drawn stays silent too.
    _playOncePerFrame(SOUND_NAME.trail_sound);

    const duration = options.duration || DEFAULT_DURATION;

    // ── The bow, and the axis it is measured on ──
    //
    // Resolved once, here, rather than per frame: `from` and `to` do not move,
    // and the perpendicular is the same for the whole flight.
    //
    // Normalised to point at +x, so a caller alternating the SIGN gets left,
    // right, left on screen whichever way the trail is travelling. Without it a
    // volley firing both up and down would flip its own alternation halfway
    // through the list and put two neighbours on the same side.
    // Straight unless the caller asks for a bend. A curve was tried on
    // 2026-09-21 and rejected — Vladimir wants a straight projectile with a
    // short tail, not an arc. Only fireMultiplierTrails bows, and it does that
    // to fan a volley apart rather than for the look.
    const bow = options.bow || 0;
    let perpX = 0;
    let perpY = 0;
    if (bow) {
      const dx = endPos.x - startPos.x;
      const dy = endPos.y - startPos.y;
      const len = Math.hypot(dx, dy) || 1;
      perpX = -dy / len;
      perpY = dx / len;
      // A perfectly horizontal flight has perpX 0; fall back to the y term so
      // the sign still means something.
      const facing = perpX !== 0 ? perpX : perpY;
      if (facing < 0) {
        perpX = -perpX;
        perpY = -perpY;
      }
    }

    // Empty on purpose — the trail is the whole effect.
    const wrapper = new PIXI.Container();
    wrapper.name = "level_charge_trail";
    wrapper.position.set(startPos.x, startPos.y);
    wrapper.zIndex = 200;
    container.addChild(wrapper);

    // emitter.target = wrapper: RevoltFX follows its position every frame, so
    // particles spawn along the path and lag behind into a tail.
    const trailEmitters = [];
    if (FXManager.isTrailReady) {
      const life = options.particleLife || TRAIL_PARTICLE_LIFE;

      // ── The emitter scale is 1, and every size below is in PIXELS ──
      //
      // RevoltFX's `scaleMod` cannot be used for size. `Particle.init` reads
      //
      //   scaleStart = rnd(startMin, startMax) * scaleMod
      //   scaleDelta = (rnd(endMin, endMax) - scaleStart) * scaleMod
      //
      // — an unscaled END minus an already-scaled START, and the difference
      // scaled a second time. So the ramp inverts as the scale rises: at 1.31 a
      // particle grew 31px -> 40px, at 1.96 it collapsed 46px -> 5px. Raising
      // the size made the streak thinner, and nothing we set for it moved.
      //
      // It also multiplies the particle's LIFETIME by the same number, which is
      // how the length came to scale quadratically with the board.
      //
      // At scaleMod 1 both formulas reduce to exactly what they say. So the
      // scale stays 1 for ever and the ramp carries the size: a texture is 47px
      // (TRAIL_DOT_PX), so a scale of frame/47 IS that many pixels wide. The
      // spawn radius and the lifetime are then plain values too, unmultiplied.
      // The ball we want, and the frame that has to be drawn to get it.
      const ball = this._trailBallPx();
      const frame = ball / TRAIL_DOT_CORE;
      const headScale = (frame * TRAIL_TAPER) / TRAIL_DOT_PX;
      const fullScale = frame / TRAIL_DOT_PX;

      // One emitter per colour, both following the same wrapper. A caller that
      // wants a single colour still names it and gets exactly that one.
      const names = options.emitterName
        ? [options.emitterName]
        : TRAIL_EMITTERS;
      for (const name of names) {
        const emitter = FXManager.emitTrail(container, wrapper, {
          emitterName: name,
          // Never anything but 1 — see the note above.
          scale: 1,
          particleLife: life,
          spawnRadius: ball * TRAIL_SPREAD,
          emitterOverrides: {
            spawnCountMin: TRAIL_SPAWN_COUNT,
            spawnCountMax: TRAIL_SPAWN_COUNT,
            spawnFrequencyMin: TRAIL_SPAWN_FREQUENCY,
            spawnFrequencyMax: TRAIL_SPAWN_FREQUENCY,
          },
          particleOverrides: {
            scaleStartMin: headScale,
            scaleStartMax: headScale,
            scaleEndMin: fullScale,
            scaleEndMax: fullScale,
            // Per emitter, and last so it wins — see TRAIL_TINTS.
            ...(TRAIL_TINTS[name] || {}),
          },
        });
        if (emitter) trailEmitters.push(emitter);
      }
    }

    // ── Resize resilience, the same shape PotCollectionValueTraveler uses ──
    //
    // Both endpoints are normalised to CELL units and the arc is rebuilt from
    // them whenever the cell size changes mid-flight. Without this a trail that
    // is in the air when the viewport changes keeps flying to where the box
    // used to be, and its arc keeps the height of the old layout.
    const normStart = {
      x: startPos.x / this.symbolWidth,
      y: startPos.y / this.symbolHeight,
    };
    const normEnd = {
      x: endPos.x / this.symbolWidth,
      y: endPos.y / this.symbolHeight,
    };

    let lastW = this.symbolWidth;
    let lastH = this.symbolHeight;
    const rebuild = () => {
      from = { x: normStart.x * lastW, y: normStart.y * lastH };
      to = { x: normEnd.x * lastW, y: normEnd.y * lastH };
    };
    let from = { x: normStart.x * lastW, y: normStart.y * lastH };
    let to = { x: normEnd.x * lastW, y: normEnd.y * lastH };
    const ease = EASING_FNS.easeInOut;

    const entry = { wrapper, tickerFn: null, trailEmitters, resolve: null };
    this._active.push(entry);

    let elapsed = 0;

    return new Promise((resolve) => {
      entry.resolve = resolve;

      const finish = (arrived) => {
        if (entry.tickerFn) {
          PIXI.Ticker.shared.remove(entry.tickerFn);
          entry.tickerFn = null;
        }
        for (const emitter of entry.trailEmitters) {
          // Drop the target BEFORE stopping it — a dead emitter still holding
          // a destroyed container is the classic crash here.
          emitter.target = null;
          // stopTrail, not killTrail: killTrail is stop(false), which recycles
          // every live particle on the spot. The tail was being guillotined at
          // the exact frame it arrived, which is most of what made the effect
          // look short. This lets the particles already in the air live out
          // their 0.2s and fade, so the streak lands and then trails off.
          // cancel() still kills — a cancelled trail should leave nothing.
          FXManager.stopTrail(emitter);
        }
        entry.trailEmitters.length = 0;
        const idx = this._active.indexOf(entry);
        if (idx !== -1) this._active.splice(idx, 1);
        if (!wrapper.destroyed) wrapper.destroy({ children: true });

        if (arrived) options.onArrival?.();
        resolve();
      };

      entry.tickerFn = () => {
        if (this._cancelled || wrapper.destroyed) {
          finish(false);
          return;
        }

        // Cheap identity check, not a resize listener: the owner updates these
        // and the next frame notices.
        if (this.symbolWidth !== lastW || this.symbolHeight !== lastH) {
          lastW = this.symbolWidth;
          lastH = this.symbolHeight;
          rebuild();
        }

        elapsed += PIXI.Ticker.shared.deltaMS;
        const t = Math.min(elapsed / duration, 1);
        const eased = ease(t);

        // Straight line by DEFAULT. The reference game fires these like arrows
        // — an arc reads as something being lobbed, which is wrong for a charge
        // that fans out to targets in different directions.
        let x = from.x + (to.x - from.x) * eased;
        let y = from.y + (to.y - from.y) * eased;

        // ── ...unless the caller asked for a bow ──
        //
        // For the one case the rule above never considered: several trails that
        // share an AXIS. A multiplier in the middle of a full reel fires at five
        // cells directly above and below it, so every trail is drawn along the
        // same line — they are not overlapping by accident, they are
        // geometrically identical, and the volley reads as one smeared streak.
        //
        // `bow` is the sideways offset at the MIDPOINT, in the layer's pixels.
        // The 4t(1-t) term is zero at both ends and 1 in the middle, so the
        // trail still leaves its source and lands on its target exactly — only
        // the path between them moves. The emitter follows the wrapper, so the
        // tail curves with it.
        if (bow) {
          const arc = 4 * eased * (1 - eased);
          x += perpX * bow * arc;
          y += perpY * bow * arc;
        }

        wrapper.position.set(x, y);

        if (t >= 1) finish(true);
      };

      PIXI.Ticker.shared.add(entry.tickerFn);
    });
  }

  /**
   * A global point in the trail container's own space.
   *
   * fireTrail takes its START in container space and its END global, because
   * callers had the symbol locally and the bulb globally. The entry flourish
   * has BOTH globally — the pip it leaves is a bone on a card hanging off a
   * spine slot — so it needs this to convert the one that is the wrong way
   * round.
   *
   * @param   {{x:number,y:number}} globalPoint
   * @returns {{x:number,y:number}|null}
   */
  toContainerSpace(globalPoint) {
    return this._toLocal(globalPoint);
  }

  _toLocal(globalPoint) {
    if (!globalPoint) return null;
    if (!this.trailContainer || this.trailContainer.destroyed) return null;
    _tempPoint.set(globalPoint.x, globalPoint.y);
    const local = this.trailContainer.toLocal(_tempPoint);
    return { x: local.x, y: local.y };
  }

  /**
   * Kill every trail at once. Their promises resolve but `onArrival` does NOT
   * fire — a cancelled trail never arrived, and lighting a bulb off one would
   * be a lie the server did not ask for.
   */
  cancel() {
    for (const entry of [...this._active]) {
      if (entry.tickerFn) {
        PIXI.Ticker.shared.remove(entry.tickerFn);
        entry.tickerFn = null;
      }
      for (const emitter of entry.trailEmitters || []) {
        emitter.target = null;
        FXManager.killTrail(emitter);
      }
      if (entry.trailEmitters) entry.trailEmitters.length = 0;
      if (!entry.wrapper.destroyed) entry.wrapper.destroy({ children: true });
      entry.resolve?.();
    }
    this._active = [];
  }

  resize(symbolWidth, symbolHeight) {
    this.symbolWidth = symbolWidth;
    this.symbolHeight = symbolHeight;
  }

  /** The bonus scene rebuilds its spine on resize — repoint at the new one. */
  setSpine(spine) {
    this.bonusSpine = spine;
  }

  destroy() {
    this._cancelled = true;
    this.cancel();
    this.trailContainer = null;
    this.bonusSpine = null;
  }
}

export default LevelChargingAnimator;
